import { useMemo } from 'react';
import type { Cue, CueProblem } from '../api/client.ts';
import { t } from '../i18n/index.ts';
import { formatDuration } from '../lib/format.ts';

/** Matches MAX_CHARS_PER_CUE in core; shown as a count, enforced as advice. */
const COMFORTABLE_CHARS = 76;
const NUDGE_MS = 100;

interface Props {
  cues: Cue[];
  problems: CueProblem[];
  onChange: (cues: Cue[]) => void;
  disabled?: boolean;
}

/**
 * Reviewing what will be burned into a video.
 *
 * This step is required, not offered. Everywhere else in this app a transcript
 * is a reading aid with the audio as the reference; a video publishes the text,
 * and the model gets names and numbers wrong. Once it is burned in and posted,
 * a correction is a public one.
 *
 * So the editor is built for correcting rather than for reading: the text is
 * editable in place, the pieces can be split and joined where the speaker
 * actually paused, and anything too long to read at a glance says so before the
 * render rather than after it.
 */
export function CueEditor({ cues, problems, onChange, disabled = false }: Props) {
  const byIndex = useMemo(() => {
    const map = new Map<number, CueProblem['kind'][]>();
    for (const problem of problems) {
      map.set(problem.index, [...(map.get(problem.index) ?? []), problem.kind]);
    }
    return map;
  }, [problems]);

  function replace(index: number, next: Partial<Cue>): void {
    onChange(cues.map((cue, i) => (i === index ? { ...cue, ...next } : cue)));
  }

  /** Splits a cue in half at the nearest word boundary to the middle. */
  function split(index: number): void {
    const cue = cues[index]!;
    const words = cue.text.trim().split(/\s+/);
    if (words.length < 2) return;

    const at = Math.round(words.length / 2);
    const first = words.slice(0, at).join(' ');
    const second = words.slice(at).join(' ');
    // The boundary follows the text, so the halves stay in step with the audio.
    const share = first.length / (first.length + second.length);
    const middle = Math.round(cue.fromMs + (cue.toMs - cue.fromMs) * share);

    onChange([
      ...cues.slice(0, index),
      { fromMs: cue.fromMs, toMs: middle, text: first },
      { fromMs: middle, toMs: cue.toMs, text: second },
      ...cues.slice(index + 1),
    ]);
  }

  /** Joins a cue with the one after it, keeping the outer bounds. */
  function mergeDown(index: number): void {
    const cue = cues[index]!;
    const next = cues[index + 1];
    if (!next) return;

    onChange([
      ...cues.slice(0, index),
      { fromMs: cue.fromMs, toMs: next.toMs, text: `${cue.text.trim()} ${next.text.trim()}`.trim() },
      ...cues.slice(index + 2),
    ]);
  }

  function remove(index: number): void {
    onChange(cues.filter((_, i) => i !== index));
  }

  if (cues.length === 0) {
    return <p className="editor-hint">{t('cues.none')}</p>;
  }

  return (
    <ol className="cue-list">
      {cues.map((cue, index) => {
        const kinds = byIndex.get(index) ?? [];
        const chars = cue.text.trim().length;
        return (
          <li key={index} className={`cue${kinds.length > 0 ? ' has-problem' : ''}`}>
            <div className="cue-times mono">
              <button
                type="button"
                disabled={disabled}
                title={t('cues.nudge_start')}
                onClick={() => replace(index, { fromMs: Math.max(0, cue.fromMs - NUDGE_MS) })}
              >−</button>
              <span>{formatDuration(cue.fromMs / 1000)}</span>
              <button
                type="button"
                disabled={disabled}
                title={t('cues.nudge_start')}
                onClick={() => replace(index, { fromMs: Math.min(cue.toMs - 100, cue.fromMs + NUDGE_MS) })}
              >+</button>
            </div>

            <textarea
              className="cue-text"
              value={cue.text}
              disabled={disabled}
              rows={2}
              dir="rtl"
              onChange={(e) => replace(index, { text: e.target.value })}
            />

            <div className="cue-side">
              <span className={`cue-chars mono${chars > COMFORTABLE_CHARS ? ' over' : ''}`}>
                {chars}
              </span>
              <div className="cue-actions">
                <button type="button" disabled={disabled} onClick={() => split(index)}>
                  {t('cues.split')}
                </button>
                <button
                  type="button"
                  disabled={disabled || index === cues.length - 1}
                  onClick={() => mergeDown(index)}
                >
                  {t('cues.merge')}
                </button>
                <button type="button" disabled={disabled} onClick={() => remove(index)}>
                  {t('cues.remove')}
                </button>
              </div>
            </div>

            {kinds.length > 0 && (
              <p className="cue-problem">
                {kinds.map((kind) => t(`cues.problem.${kind}`)).join(' · ')}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}
